/**
 * list_templates Tool - Lists available templates for a project
 */

import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import type { JobClient } from '../clients/job-client.js';

export interface ListTemplatesArgs {
  projectId: string;
  limit?: number;
}

export const listTemplatesToolDefinition: Tool = {
  name: 'list_templates',
  description: `Lists available templates for a project.

Templates are reusable test configurations that define test instructions, default URL, output schema, duration settings, and more.

Use templates to quickly create tests with pre-defined configurations.
Pass the template name to create_job's template parameter to use one.`,
  inputSchema: {
    type: 'object',
    properties: {
      projectId: {
        type: 'string',
        description: 'Project ID to list templates for (required).',
      },
      limit: {
        type: 'number',
        description: 'Maximum number of templates to return. Default: 50.',
      },
    },
    required: ['projectId'],
  },
};

export async function handleListTemplates(
  args: ListTemplatesArgs,
  jobClient: JobClient
): Promise<{ content: Array<{ type: 'text'; text: string }>; isError?: boolean }> {
  try {
    const data = await jobClient.listTemplates(args.projectId, args.limit);

    if (data.items.length === 0) {
      return {
        content: [{
          type: 'text',
          text: `No templates found for this project.

You can still create jobs without templates by providing url and description directly to create_job.`,
        }],
      };
    }

    const templateList = data.items.map(t => {
      let entry = `- "${t.name}"`;
      if (t.url) entry += `\n  Default URL: ${t.url}`;
      if (t.testDescription) entry += `\n  Instructions: ${t.testDescription.substring(0, 100)}${t.testDescription.length > 100 ? '...' : ''}`;
      return entry;
    }).join('\n\n');

    return {
      content: [{
        type: 'text',
        text: `Found ${data.items.length} template(s):

${templateList}

To use a template: create_job(template: "template-name-here", ...)
Any fields you provide directly will override template values.`,
      }],
    };
  } catch (error) {
    const err = error as Error & { status?: number };

    return {
      content: [{
        type: 'text',
        text: `Failed to list templates

Error: ${err.message}
${err.status ? `Status: ${err.status}` : ''}`,
      }],
      isError: true,
    };
  }
}
